import clsx from "clsx";
import {
  InputHTMLAttributes,
  ReactNode,
  RefObject,
  forwardRef,
  ForwardRefRenderFunction,
  useId,
} from "react";

import { SELECTORS } from "@ui/config";
import { sharedStyles } from "@ui/shared-styles";

import * as styles from "./FormFieldText.css";

type Props = {
  name: string;
  label: string;
  className?: string;
  inputClassName?: string;
  innerContainerClassName?: string;
  actionButtonContainerClassName?: string;
  actionButton?: ReactNode;
  id?: string;
  ref?: RefObject<HTMLInputElement | null>;
  hideLabel?: boolean;
  error?: string;
} & InputHTMLAttributes<HTMLInputElement>;

const FormFieldText: ForwardRefRenderFunction<HTMLInputElement, Props> = (
  {
    label,
    className,
    actionButton,
    id,
    hideLabel,
    inputClassName,
    innerContainerClassName,
    actionButtonContainerClassName,
    error,
    ...props
  },
  ref,
) => {
  const defaultId = useId();
  const resolvedId = id || defaultId;
  return (
    <div className={clsx(SELECTORS.FormFieldText, styles.container, className)}>
      <label
        htmlFor={resolvedId}
        className={clsx(
          styles.label,
          hideLabel && sharedStyles.accessibility.srOnly,
        )}
      >
        {label}
      </label>
      <div className={clsx(styles.innerContainer, innerContainerClassName)}>
        <input
          id={resolvedId}
          ref={ref}
          className={clsx(
            styles.input,
            inputClassName,
            error && sharedStyles.form.errorField,
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `${resolvedId}-error` : undefined}
          {...props}
        />
        {actionButton && (
          <span
            className={clsx(
              styles.buttonContainer,
              actionButtonContainerClassName,
            )}
          >
            {actionButton}
          </span>
        )}
      </div>
      {error && (
        <span id={`${resolvedId}-error`} className={sharedStyles.form.error}>
          {error}
        </span>
      )}
    </div>
  );
};

export default forwardRef<HTMLInputElement, Props>(FormFieldText);
